import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Avatar, AvatarGroup, CircularProgress, alpha } from '@mui/material';
import {
  ChevronRight as ArrowIcon,
  LinkedIn as LinkedInIcon,
  Article as BlogIcon,
  Twitter as TwitterIcon,
  Email as EmailIcon,
  MoreHoriz as OtherIcon,
  EventNote as RecurringIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { ContentTask } from '../../types/ContentTracking';

interface User {
  _id: string;
  username: string;
}

interface PendingTasksListProps {
  tasks: ContentTask[];
  users: User[];
  loading: boolean;
  currentUserRole: string;
  currentUsername: string;
  selectedUserFromDashboard?: string;
}

const PendingTasksList: React.FC<PendingTasksListProps> = ({
  tasks,
  loading,
  currentUserRole,
  currentUsername,
  selectedUserFromDashboard,
}) => {
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const isAdminOrSales = currentUserRole === 'admin' || currentUserRole === 'sales';
  const isSuperAdmin = currentUserRole === 'superadmin';

  useEffect(() => {
    if (isSuperAdmin && selectedUserFromDashboard) {
      setSelectedUser(selectedUserFromDashboard);
    } else if (isAdminOrSales) {
      setSelectedUser(currentUsername);
    }
  }, [selectedUserFromDashboard, isSuperAdmin, isAdminOrSales, currentUsername]);

  const pendingApprovalCount = useMemo(() => {
    return tasks.filter((task) => task.status === 'pending_approval').length;
  }, [tasks]);

  const pendingTasks = useMemo(() => {
    return tasks.filter(
      (task) =>
        task.status === 'pending' ||
        task.status === 'in-progress' ||
        task.status === 'pending_approval'
    );
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let list = pendingTasks;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const currentUserRoleLower = currentUserRole?.toLowerCase();
    const isSuperAdminLocal = currentUserRoleLower === 'superadmin';
    const isAdminOrSalesLocal =
      currentUserRoleLower === 'admin' || currentUserRoleLower === 'sales';

    // Only filter by date if NOT superadmin (Superadmin sees all pending tasks)
    if (!isSuperAdminLocal) {
      list = list.filter((task) => {
        const dueDate = new Date(task.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate <= today;
      });
    }

    if (isAdminOrSalesLocal || isSuperAdminLocal) {
      list = list.filter((task) => {
        // Always show pending_approval tasks for admins/superadmins
        if (task.status === 'pending_approval') return true;

        // Otherwise filter by user if specific user selected
        if (selectedUser && selectedUser !== 'all') {
          const searchUser = selectedUser.toLowerCase().trim();
          return task.assignedTo.some(
            (user) =>
              user.username?.toLowerCase().trim() === searchUser ||
              user.email?.toLowerCase().trim() === searchUser
          );
        }

        // For non-superadmin, filter by current user if no user selected?
        // (Usually handled by useEffect setting selectedUser)
        return true;
      });
    }

    return list;
  }, [pendingTasks, selectedUser, currentUsername, currentUserRole]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#f1b44c';
      case 'in-progress':
        return '#50a5f1';
      case 'pending_approval':
        return '#f46a6a';
      default:
        return '#aaa';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'linkedin post':
        return <LinkedInIcon sx={{ fontSize: '1.1rem' }} />;
      case 'blog':
        return <BlogIcon sx={{ fontSize: '1.1rem' }} />;
      case 'twitter post':
        return <TwitterIcon sx={{ fontSize: '1.1rem' }} />;
      case 'email':
        return <EmailIcon sx={{ fontSize: '1.1rem' }} />;
      case 'recurring_task':
        return <RecurringIcon sx={{ fontSize: '1.1rem' }} />;
      default:
        return <OtherIcon sx={{ fontSize: '1.1rem' }} />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'linkedin post':
        return '#2a4d8b';
      case 'blog':
        return '#4d3b2b';
      case 'recurring_task':
        return '#2b3e4d';
      default:
        return '#3b3b3b';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Today';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={30} sx={{ color: 'rgba(255,255,255,0.2)' }} />
      </Box>
    );
  }

  if (filteredTasks.length === 0) {
    return (
      <Box sx={{ width: '100%', position: 'relative', pt: 1 }}>
        {/* Header even when empty */}
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                bgcolor: '#556ee6',
                borderRadius: '4px',
                boxShadow: '0 0 15px rgba(85, 110, 230, 0.4)',
              }}
            />
            <Typography
              sx={{
                fontWeight: 900,
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '2.5px',
                fontFamily: 'Lato, sans-serif',
              }}
            >
              Today&apos;s Agenda
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {pendingApprovalCount > 0 && (
              <Box
                sx={{
                  px: 1,
                  py: 0.4,
                  bgcolor: alpha('#f46a6a', 0.15),
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: alpha('#f46a6a', 0.3),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    color: '#f46a6a',
                    fontSize: '0.6rem',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Pending for Approval
                </Typography>
                <Typography
                  sx={{
                    color: '#f46a6a',
                    fontSize: '0.75rem',
                    fontWeight: 900,
                  }}
                >
                  {pendingApprovalCount}
                </Typography>
              </Box>
            )}
            <Typography
              onClick={() => (window.location.href = '/content-tracking')}
              sx={{
                color: 'rgba(255,255,255,0.3)',
                fontSize: '0.62rem',
                fontWeight: 800,
                cursor: 'pointer',
                transition: '0.2s',
                textTransform: 'uppercase',
                letterSpacing: '1.2px',
                '&:hover': { color: '#fff' },
              }}
            >
              View More
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            background: 'rgba(255,255,255,0.01)',
            borderRadius: 4,
            border: '1px dashed rgba(255,255,255,0.05)',
          }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
            No pending tasks for today
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', position: 'relative', pt: 1 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              bgcolor: '#556ee6',
              borderRadius: '4px',
              boxShadow: '0 0 15px rgba(85, 110, 230, 0.4)',
            }}
          />
          <Typography
            sx={{
              fontWeight: 900,
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '2.5px',
              fontFamily: 'Lato, sans-serif',
            }}
          >
            Today&apos;s Agenda
          </Typography>
          <Box
            sx={{
              px: 1.2,
              py: 0.4,
              bgcolor: '#2b3152',
              borderRadius: '6px',
              ml: 1,
            }}
          >
            <Typography sx={{ color: '#8b93ff', fontSize: '0.75rem', fontWeight: 900 }}>
              {filteredTasks.length}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {pendingApprovalCount > 0 && (
            <Box
              sx={{
                px: 1.2,
                py: 0.4,
                bgcolor: alpha('#f46a6a', 0.15),
                borderRadius: '6px',
                border: '1px solid',
                borderColor: alpha('#f46a6a', 0.3),
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  color: '#f46a6a',
                  fontSize: '0.6rem',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Pending for Approval
              </Typography>
              <Typography
                sx={{
                  color: '#f46a6a',
                  fontSize: '0.75rem',
                  fontWeight: 900,
                }}
              >
                {pendingApprovalCount}
              </Typography>
            </Box>
          )}
          <Typography
            onClick={() => (window.location.href = '/content-tracking')}
            sx={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: '0.62rem',
              fontWeight: 800,
              cursor: 'pointer',
              transition: '0.2s',
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              '&:hover': { color: '#fff' },
            }}
          >
            View More
          </Typography>
        </Box>
      </Box>

      {/* Timeline List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, position: 'relative' }}>
        {/* Timeline Path Line */}
        <Box
          sx={{
            position: 'absolute',
            left: 28,
            top: 40,
            bottom: 40,
            width: '1px',
            background: 'rgba(255, 255, 255, 0.05)',
            zIndex: 1,
          }}
        />

        <AnimatePresence>
          {filteredTasks.slice(0, 3).map((task, index) => (
            <Box
              key={task._id}
              component={motion.div}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              onClick={() => (window.location.href = '/content-tracking')}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 2.2,
                p: 2,
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                zIndex: 2,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.04)',
                  borderColor: alpha(getStatusColor(task.status), 0.2),
                  transform: 'scale(1.005)',
                  '& .task-arrow': { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '16px',
                  bgcolor: getCategoryColor(task.category),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: task.category?.toLowerCase() === 'linkedin post' ? '#50a5f1' : '#f1b44c',
                  border: `1px solid ${alpha('#fff', 0.05)}`,
                  flexShrink: 0,
                }}
              >
                {getCategoryIcon(task.category)}
              </Box>

              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.4 }}>
                  <Typography
                    sx={{
                      color: '#efefef',
                      fontSize: '1rem',
                      fontWeight: 700,
                      fontFamily: 'Lato, sans-serif',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {task.title}
                  </Typography>
                  {index === 0 && (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.2,
                        bgcolor: '#f1b44c',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#000',
                          fontSize: '0.55rem',
                          fontWeight: 900,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Next Up
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    {formatDate(task.dueDate)}
                  </Typography>
                  <Box
                    sx={{ width: 3, height: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}
                  />
                  <Typography
                    sx={{
                      color: getStatusColor(task.status),
                      fontSize: '0.7rem',
                      fontWeight: 900,
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                    }}
                  >
                    {task.status.replace('_', ' ')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AvatarGroup
                  max={1}
                  sx={{
                    '& .MuiAvatar-root': {
                      width: 28,
                      height: 28,
                      fontSize: '0.7rem',
                      border: '2px solid #1c1d27',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  {task.assignedTo.map((u) => (
                    <Avatar key={u._id}>{u.username[0].toUpperCase()}</Avatar>
                  ))}
                </AvatarGroup>
                <ArrowIcon
                  className='task-arrow'
                  sx={{
                    fontSize: '1.1rem',
                    color: 'rgba(255,255,255,0.1)',
                    opacity: 0.5,
                    transition: '0.3s ease',
                  }}
                />
              </Box>
            </Box>
          ))}
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default PendingTasksList;
